import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import {
    OdataValueHelperInstanceToken,
    OdataValueHelperInterface,
} from '../../../interfaces/odata-value-helper.interface';
import { hydrateSlice, persistSlice, sliceHydrated } from '../../persistence';
import {
    initValueHelpers,
    valueHelpersFailed,
    valueHelpersLoaded,
    valueHelpersLoadedFromStorage,
} from '../actions/value-helper.actions';
import { selectValueHelperLoaded } from '../selectors/value-helper.selector';
import { VALUE_HELPER_FEATURE_KEY, ValueHelperState } from '../value-helper-collection.state';

/**
 * Owns the value-helper load flow and the persistence mirror. `loadValueHelpers$` fetches the
 * reference data from OData on every `initValueHelpers` dispatch (initial boot and after sync);
 * `persistValueHelpers$` caches the slice after each backend load; `hydrateValueHelpersOnInit$`
 * reads the cache back at startup so value helpers are available offline while the network
 * request is in flight. Both hydrate steps are guarded on {@link selectValueHelperLoaded} so a
 * slower cache read can never overwrite fresher backend data.
 */
@Injectable()
export class ValueHelperEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        @Inject(OdataValueHelperInstanceToken) private readonly odataValueHelperService: OdataValueHelperInterface,
    ) {}

    /** Fetch value helpers from the backend and populate the slice. */
    public loadValueHelpers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initValueHelpers),
            switchMap(() =>
                this.odataValueHelperService.getValueHelpersState().pipe(
                    map((valueHelpers) =>
                        valueHelpersLoaded({
                            valueHelpers,
                        }),
                    ),
                    catchError(() => of(valueHelpersFailed())),
                ),
            ),
        ),
    );

    /** Cache the slice whenever the backend load populates it. */
    public persistValueHelpers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(valueHelpersLoaded),
            map(() => persistSlice({ featureKey: VALUE_HELPER_FEATURE_KEY })),
        ),
    );

    /** Rehydrate from the cache on init, but only while the backend has not yet populated the slice. */
    public hydrateValueHelpersOnInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initValueHelpers),
            concatLatestFrom(() => this.store.select(selectValueHelperLoaded)),
            filter(([, loaded]) => !loaded),
            map(() => hydrateSlice({ featureKey: VALUE_HELPER_FEATURE_KEY })),
        ),
    );

    /** Bridge the generic hydrate result into the value-help slice, unless the backend already won. */
    public applyHydratedValueHelpers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === VALUE_HELPER_FEATURE_KEY),
            concatLatestFrom(() => this.store.select(selectValueHelperLoaded)),
            filter(([, loaded]) => !loaded),
            map(([{ data }]) =>
                valueHelpersLoadedFromStorage({ valueHelpers: (data as ValueHelperState[] | undefined) ?? [] }),
            ),
        ),
    );
}
